/**
 * Columna derecha de la bandeja: ficha del contacto y acciones de la
 * conversacion (estado, asignacion, etiquetas y control del bot).
 */
import { useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import type { ConversationDetail, TeamMemberView } from '@shared/api-types.ts';
import type { BotMode } from '@domain/types.ts';
import {
  BOT_MODES,
  CONVERSATION_STATUSES,
  CONVERSATION_STATUS_LABELS,
  HANDOFF_REASON_LABELS,
  MEMBER_ROLE_LABELS,
} from '@domain/types.ts';
import type { UpdateConversationRequest } from '../api.ts';
import { formatAttributeValue, formatDateTime, formatPhone } from '../format.ts';
import {
  BOT_MODE_HINTS,
  BOT_MODE_LABELS,
  OPT_IN_LABELS,
  conversationDisplayName,
} from '../state.ts';
import { Avatar } from './Avatar.tsx';
import { Chip, EmptyState, ErrorBanner, Field, SegmentedControl, SkeletonBlock } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

const OPT_IN_TONES: Record<string, 'success' | 'danger' | 'neutral'> = {
  opted_in: 'success',
  opted_out: 'danger',
  unknown: 'neutral',
};

export function ConversationPanel({
  detail,
  loading,
  error,
  team,
  currentUserId,
  patching,
  onRetry,
  onPatch,
  onClose,
}: {
  detail: ConversationDetail | null;
  loading: boolean;
  error: string | null;
  team: TeamMemberView[];
  currentUserId: string;
  patching: boolean;
  onRetry: () => void;
  onPatch: (patch: UpdateConversationRequest) => void;
  onClose: () => void;
}): ReactElement {
  const [tagDraft, setTagDraft] = useState('');

  const commitTag = (): void => {
    if (!detail) return;
    const value = tagDraft.trim().replace(/,+$/, '');
    setTagDraft('');
    if (!value || detail.tags.includes(value)) return;
    onPatch({ tags: [...detail.tags, value] });
  };

  const handleTagKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitTag();
      return;
    }
    if (event.key === 'Backspace' && tagDraft === '' && detail && detail.tags.length > 0) {
      const last = detail.tags[detail.tags.length - 1];
      if (last !== undefined) onPatch({ tags: detail.tags.filter((tag) => tag !== last) });
    }
  };

  if (loading) {
    return (
      <aside className="panel" aria-label="Ficha de la conversacion">
        <SkeletonBlock height={160} />
        <SkeletonBlock height={140} />
        <SkeletonBlock height={180} />
      </aside>
    );
  }

  if (error) {
    return (
      <aside className="panel" aria-label="Ficha de la conversacion">
        <ErrorBanner message={error} onRetry={onRetry} />
      </aside>
    );
  }

  if (!detail) {
    return (
      <aside className="panel" aria-label="Ficha de la conversacion">
        <EmptyState
          icon="info"
          title="Ninguna conversacion seleccionada"
          description="Elige una conversacion de la lista para ver la ficha del contacto y las acciones disponibles."
        />
      </aside>
    );
  }

  const name = conversationDisplayName(detail);
  const attributes = Object.entries(detail.contactFull.attributes);
  const optInLabel = OPT_IN_LABELS[detail.contactFull.optInStatus] ?? detail.contactFull.optInStatus;
  const optInTone = OPT_IN_TONES[detail.contactFull.optInStatus] ?? 'neutral';
  const otherMembers = team.filter((member) => member.userId !== currentUserId);

  return (
    <aside className="panel" aria-label="Ficha de la conversacion">
      <div className="panel__header">
        <h2 className="panel__heading">Conversacion</h2>
        <button
          type="button"
          className="button button--ghost button--sm panel__close"
          onClick={onClose}
          aria-label="Cerrar la ficha"
        >
          <Icon name="close" />
        </button>
      </div>

      <section className="panel__contact">
        <Avatar name={name} seed={detail.contact.id} size="lg" />
        <div className="panel__contacttext">
          <p className="panel__name">{name}</p>
          <p className="panel__phone">{formatPhone(detail.contact.waId)}</p>
          <div className="panel__chips">
            <Chip tone={optInTone}>{optInLabel}</Chip>
            <Chip tone={detail.window.isOpen ? 'success' : 'warning'}>
              {detail.window.isOpen ? 'Ventana abierta' : 'Ventana cerrada'}
            </Chip>
          </div>
        </div>
      </section>

      <section className="panel__section">
        <h3 className="panel__title">Ventana de atencion (24 h)</h3>
        <div className={detail.window.isOpen ? 'windowbox windowbox--open' : 'windowbox windowbox--closed'}>
          <p className="windowbox__label">{detail.window.label}</p>
          <p className="windowbox__hint">
            {detail.window.isOpen
              ? `Se puede responder con texto libre. Se cierra el ${formatDateTime(detail.window.expiresAt)}.`
              : 'Solo se pueden enviar plantillas aprobadas hasta que el cliente vuelva a escribir.'}
          </p>
        </div>
      </section>

      <section className="panel__section">
        <h3 className="panel__title">Gestion</h3>

        <Field label="Estado" htmlFor="panel-status">
          <select
            id="panel-status"
            className="select"
            value={detail.status}
            disabled={patching}
            onChange={(event) =>
              onPatch({ status: event.target.value as ConversationDetail['status'] })
            }
          >
            {CONVERSATION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {CONVERSATION_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Asignada a" htmlFor="panel-assignee">
          <select
            id="panel-assignee"
            className="select"
            value={detail.assignee?.id ?? ''}
            disabled={patching}
            onChange={(event) => onPatch({ assigneeUserId: event.target.value || null })}
          >
            <option value="">Sin asignar</option>
            <option value={currentUserId}>Mi (yo)</option>
            {otherMembers.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name} · {MEMBER_ROLE_LABELS[member.role]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Etiquetas" htmlFor="panel-tags" hint="Pulsa Enter o coma para anadir.">
          <div className="tageditor">
            {detail.tags.map((tag) => (
              <span className="tag tag--removable" key={tag}>
                {tag}
                <button
                  type="button"
                  className="tag__remove"
                  onClick={() => onPatch({ tags: detail.tags.filter((item) => item !== tag) })}
                  aria-label={`Quitar la etiqueta ${tag}`}
                  disabled={patching}
                >
                  <Icon name="close" />
                </button>
              </span>
            ))}
            <input
              id="panel-tags"
              className="tageditor__input"
              value={tagDraft}
              placeholder={detail.tags.length === 0 ? 'vip, facturacion…' : 'Anadir…'}
              onChange={(event) => setTagDraft(event.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={commitTag}
              disabled={patching}
            />
          </div>
        </Field>
      </section>

      <section className="panel__section">
        <h3 className="panel__title">Asistente de IA</h3>
        <SegmentedControl<BotMode>
          ariaLabel="Modo del bot"
          value={detail.botMode}
          onChange={(mode) => onPatch({ botMode: mode })}
          options={BOT_MODES.map((mode) => ({
            value: mode,
            label: BOT_MODE_LABELS[mode],
            title: BOT_MODE_HINTS[mode],
          }))}
        />
        <p className="panel__hint">{BOT_MODE_HINTS[detail.botMode]}</p>

        {detail.botMode === 'auto' ? (
          <p className="panel__hint">
            Le quedan {detail.botRepliesRemaining} respuestas automaticas antes de escalar a una persona.
          </p>
        ) : null}

        {detail.handoffReason ? (
          <div className="handoff">
            <Chip tone="warning">Ultimo escalado</Chip>
            <p className="handoff__text">{HANDOFF_REASON_LABELS[detail.handoffReason]}</p>
          </div>
        ) : null}
      </section>

      <section className="panel__section">
        <h3 className="panel__title">Contacto y cuenta</h3>
        <dl className="kv">
          <div className="kv__row">
            <dt>Cuenta</dt>
            <dd>
              {detail.account.name}
              {detail.account.displayPhoneNumber ? ` · ${detail.account.displayPhoneNumber}` : ''}
            </dd>
          </div>
          <div className="kv__row">
            <dt>Ultimo mensaje del cliente</dt>
            <dd>{detail.lastInboundAt ? formatDateTime(detail.lastInboundAt) : 'Nunca'}</dd>
          </div>
          <div className="kv__row">
            <dt>Visto por ultima vez</dt>
            <dd>
              {detail.contactFull.lastSeenAt ? formatDateTime(detail.contactFull.lastSeenAt) : 'Sin datos'}
            </dd>
          </div>
          <div className="kv__row">
            <dt>Conversacion creada</dt>
            <dd>{formatDateTime(detail.createdAt)}</dd>
          </div>
          <div className="kv__row">
            <dt>Respuestas seguidas del bot</dt>
            <dd>
              {detail.consecutiveBotReplies} de {detail.consecutiveBotReplies + detail.botRepliesRemaining}
            </dd>
          </div>
        </dl>

        <h4 className="panel__subtitle">Atributos del contacto</h4>
        {attributes.length === 0 ? (
          <p className="panel__hint">Este contacto no tiene atributos guardados.</p>
        ) : (
          <dl className="kv">
            {attributes.map(([key, value]) => (
              <div className="kv__row" key={key}>
                <dt>{key}</dt>
                <dd>{formatAttributeValue(value)}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    </aside>
  );
}
